import * as React from 'react';
import { useJsonForms, withJsonFormsControlProps } from '@jsonforms/react';
import Rating from '../Rating/Rating';
import { SchemaUtils } from "../../util/SchemaUtils";
import { ControlValueFactory } from "../../factory/ControlValueFactory";
import { Component } from "../../enum/Component";
import { useMemo, useCallback } from 'react';

interface RatingControlProps {
    data: any;
    label: any;
    description?: any;
    handleChange(path: string, value: number | null): void;
    path: string;
    uischema: any;
    schema: any;
    errors: any;
    rootSchema: any;
    appValidated?: boolean;
}

const RatingControl = ({
                           errors,
                           uischema,
                           schema,
                           handleChange,
                           path,
                           data,
                           rootSchema,
                           appValidated
                       }: RatingControlProps) => {

    const jsonForms = useJsonForms();

    const enabled = useMemo(() =>
            SchemaUtils.isComponentEnabled(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const visible = useMemo(() =>
            SchemaUtils.isComponentVisible(uischema, jsonForms.core!.data),
        [uischema, jsonForms.core!.data]
    );

    const required = useMemo(() =>
            SchemaUtils.isRequired(path, rootSchema),
        [path, rootSchema]
    );

    const value = useMemo(() => {
        const val = ControlValueFactory.getControlValue(
            Component.Rating,
            data,
            uischema.scope,
            jsonForms,
            enabled && visible
        );
        return val;
    }, [data, uischema.scope, jsonForms, enabled, visible]);

    const handleRatingChange = useCallback((event: React.SyntheticEvent, newValue: number | null) => {
        handleChange(path, newValue);
    }, [handleChange, path]);

    const precision = useMemo(() => {
        return schema.precision || 1;
    }, [schema]);

    const ratingProps = useMemo(() => ({
        error: errors,
        value: value,
        required: required,
        label: uischema.label,
        description: schema.description,
        onChange: handleRatingChange,
        minimum: schema.minimum,
        maximum: schema.maximum,
        precision: precision,
        enabled: enabled,
        visible: visible,
        appValidated: appValidated,
        scope: uischema.scope,
        size: 'large' as const
    }), [
        errors,
        value,
        required,
        uischema.label,
        schema.description,
        handleRatingChange,
        schema.minimum,
        schema.maximum,
        precision,
        enabled,
        visible,
        appValidated,
        uischema.scope
    ]);

    return <Rating {...ratingProps} />;
};

RatingControl.displayName = 'RatingControl';

export default withJsonFormsControlProps(RatingControl);
